import { useAsyncOperation } from "@core/hooks/use-async-operation";
import { useI18n } from "@core/i18n/i18n";
import { useNavigate } from "@core/routing/use-navigate";
import { programOperationsManager } from "@core/service-container";
// ProgramId import removed - now using string type instead
import {
	AnalyticsExtendProgramLocation,
	sendAnalyticsEventDuplicateProgramAsDraft,
	sendAnalyticsEventDuplicateProgramAsTemplate,
	sendAnalyticsEventExtendProgram,
} from "@domain/program/program-analytics";
import { GlobalProgramStatusType } from "@domain/program/program-status/program-status";
import { ToastContext } from "@ui/components/common/toast-provider";
import { RoutesPath } from "@ui/navigation/routes";
import { ProgramListTabNames } from "@ui/pages/program/components/tabs";
import { isNil } from "@utils/assert";
import { booleanToString } from "@utils/boolean-utils";
import { useCallback, useContext } from "react";

interface DuplicateAsDraftProgramProps {
	patientId: string;
	programName: string;
	programId: string;
	onSuccess?: (newProgramId: string) => void;
}

interface DuplicateAsTemplateProgramProps {
	patientId: string;
	programId: string;
	templateName: string;
}

export function useProgramActions(): {
	duplicateAsDraftProgram: (props: DuplicateAsDraftProgramProps) => Promise<void>;
	duplicateAsTemplateProgram: (props: DuplicateAsTemplateProgramProps) => Promise<void | undefined>;
	loading: boolean;
	extendProgram: (
		location: AnalyticsExtendProgramLocation,
		patientId: string,
		patientAnalyticsId: string,
		programId: string | undefined,
		globalProgramStatus: GlobalProgramStatusType,
	) => void;
} {
	const { showToast } = useContext(ToastContext);
	const { format } = useI18n();
	const navigate = useNavigate();

	const [duplicateAsDraftProgram, duplicateAsDraftLoading] = useAsyncOperation(
		async ({ patientId, programId, programName, onSuccess }: DuplicateAsDraftProgramProps) => {
			try {
				const newProgramId = await programOperationsManager.performDuplicateAsDraftOperation(patientId, programId);
				showToast(
					format("global.duplicateProgramOperation.successToast.title"),
					format("global.duplicateProgramOperation.successToast.message", {
						program: programName,
					}),
					"success",
				);
				sendAnalyticsEventDuplicateProgramAsDraft(programId);
				onSuccess?.(newProgramId);
			} catch (e) {
				console.error(e);
				showToast(format("global.errorToast.title"), format("global.duplicateProgramOperation.errorToast.message"));
			}
		},
		{
			deps: [format, showToast],
			minimumDuration: 3000,
		},
	);

	const [duplicateAsTemplateProgram, duplicateAsTemplateLoading] = useAsyncOperation(
		async ({ patientId, programId, templateName }: DuplicateAsTemplateProgramProps) => {
			const newProgramId = await programOperationsManager.performTransformToTemplateOperation(
				patientId,
				programId,
				templateName,
			);
			sendAnalyticsEventDuplicateProgramAsTemplate(programId);
			navigate(RoutesPath.ProgramList, {
				state: { newProgramId },
				query: { active: ProgramListTabNames.Templates },
			});
			showToast(
				format("global.transformProgramToTemplateOperation.successToast.title"),
				format("global.transformProgramToTemplateOperation.successToast.message", {
					template: templateName,
				}),
				"success",
			);
		},
		{
			deps: [format, showToast],
			minimumDuration: 3000,
		},
	);

	const extendProgram = useCallback(
		(
			location: AnalyticsExtendProgramLocation,
			patientId: string,
			patientAnalyticsId: string,
			programId: string | undefined,
			globalProgramStatus: GlobalProgramStatusType,
		) => {
			if (isNil(programId)) {
				throw new Error("programId undefined");
			}
			sendAnalyticsEventExtendProgram(location, patientAnalyticsId, programId);
			navigate(RoutesPath.ProgramBuilder, {
				route: { patientId },
				query: {
					programId,
					openScheduleModal: booleanToString(
						globalProgramStatus === GlobalProgramStatusType.EndingSoonProgramWithNoNext,
					),
				},
			});
		},
		[navigate],
	);

	const loading = duplicateAsDraftLoading || duplicateAsTemplateLoading;

	return { duplicateAsDraftProgram, duplicateAsTemplateProgram, loading, extendProgram };
}
